import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n, attrLabel } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { KindBadge } from "../components/KindBadge";
import { ArrowLeft, Pencil, FileText, ShieldCheck, ExternalLink, HeartHandshake, Link2, Printer, Download, Star, Trash2, Upload, Loader2, Languages } from "lucide-react";
import { toast } from "sonner";

function fmtDob(v) {
  if (!v) return v;
  const p = String(v).split("-");
  if (p.length === 3) return `${p[2]}/${p[1]}/${p[0]}`;
  if (p.length === 2) return `${p[1]}/${p[0]}`;
  return p[0];
}

function Detail({ label, value }) {
  if (value === null || value === undefined || value === "" || value === "unknown") return null;
  return (
    <div data-testid="detail-field">
      <div className="font-body text-xs uppercase tracking-[0.12em] text-[#6B7F82]">{label}</div>
      <div className="mt-0.5 font-body text-sm text-[#1A2024]">{value}</div>
    </div>
  );
}

function Block({ title, children }) {
  const has = React.Children.toArray(children).some(Boolean);
  if (!has) return null;
  return (
    <section className="rounded-lg border border-[#E2E8F0] bg-white p-6 shadow-sm">
      <h3 className="font-heading text-sm font-semibold uppercase tracking-[0.16em] text-[#6B7F82]">{title}</h3>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">{children}</div>
    </section>
  );
}

export default function CaseDetailPage({ kind }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const isAnte = kind === "ante_mortem";
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePhoto, setActivePhoto] = useState(null);
  const [pendingPhotos, setPendingPhotos] = useState(null);
  const [trans, setTrans] = useState(null);
  const [showTrans, setShowTrans] = useState(false);
  const [transLoading, setTransLoading] = useState(false);

  // Privacy: families may never see details of unidentified (post-mortem) records.
  const blocked = !isAnte && user?.role === "family";

  useEffect(() => {
    if (blocked) { setLoading(false); return; }
    const base = isAnte ? "/ante-mortem" : "/post-mortem";
    api.get(`${base}/${id}`)
      .then((r) => {
        setC(r.data);
        const list = (r.data.photo_paths && r.data.photo_paths.length ? r.data.photo_paths
          : (r.data.photo_path ? [r.data.photo_path] : []));
        setActivePhoto(list[0] || null);
      })
      .catch(() => { toast.error("Could not load this record."); navigate("/dashboard"); })
      .finally(() => setLoading(false));
  }, [id, isAnte, blocked, navigate]);

  useEffect(() => {
    if (c) {
      const name = isAnte ? (c.full_name || c.reference_code) : (c.reference_code || "Unidentified");
      if (name) document.title = `EuroFind — ${name}`;
    }
    return () => { document.title = "EuroFind"; };
  }, [c, isAnte]);

  if (blocked) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16 text-center" data-testid="detail-privacy-block">
        <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[#4A7C59]/12 text-[#4A7C59]">
          <ShieldCheck size={26} />
        </span>
        <h1 className="mt-5 font-heading text-2xl font-semibold text-[#1A2024]">{t("detail.privacy_title")}</h1>
        <p className="mx-auto mt-3 max-w-lg font-body text-base text-[#5C6873]">{t("detail.privacy_body")}</p>
        <Link to="/matches" className="mt-6 inline-block" data-testid="detail-privacy-cta">
          <Button className="bg-[#2E3A46] text-white hover:bg-[#1A2024]"><HeartHandshake size={16} className="mr-1" /> {t("detail.privacy_cta")}</Button>
        </Link>
      </div>
    );
  }

  if (loading) return <div className="mx-auto max-w-3xl px-6 py-10 font-body text-sm text-[#5C6873]" data-testid="detail-loading">{t("common.loading")}</div>;
  if (!c) return null;

  const isOwner = c.owner_id === user?.id;
  const photos = (c.photo_paths && c.photo_paths.length ? c.photo_paths
    : (c.photo_path ? [c.photo_path] : []));
  const canEdit = isOwner && (isAnte
    ? (user?.role === "family" || user?.role === "authority")
    : user?.role === "authority");
  const editPath = `/${isAnte ? "ante-mortem" : "post-mortem"}/${id}/edit`;
  const title = isAnte ? (c.full_name || c.reference_code) : (c.reference_code || "Unidentified record");

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success(t("detail.copied"));
    } catch {
      toast.error(window.location.href);
    }
  };

  const kindPath = isAnte ? "ante_mortem" : "post_mortem";
  const canManagePhotos = canEdit || user?.role === "admin";
  const displayPhotos = pendingPhotos ?? photos;

  const stagePhotos = (list) => {
    setPendingPhotos(list);
    if (!list.includes(activePhoto)) setActivePhoto(list[0] || null);
  };

  const setAsMain = (p) => stagePhotos([p, ...displayPhotos.filter((x) => x !== p)]);
  const removePhoto = (p) => stagePhotos(displayPhotos.filter((x) => x !== p));

  const savePhotos = async () => {
    try {
      const { data } = await api.put(`/cases/${kindPath}/${id}/photos`, { photo_paths: pendingPhotos });
      setC(data);
      setPendingPhotos(null);
      const list = data.photo_paths && data.photo_paths.length ? data.photo_paths : (data.photo_path ? [data.photo_path] : []);
      if (!list.includes(activePhoto)) setActivePhoto(list[0] || null);
      toast.success(t("detail.photos_saved"));
    } catch (e) {
      toast.error(t("detail.photos_error"));
    }
  };

  const cancelPhotos = () => {
    setPendingPhotos(null);
    if (!photos.includes(activePhoto)) setActivePhoto(photos[0] || null);
  };

  const addPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      stagePhotos([...displayPhotos, data.path]);
    } catch (err) {
      toast.error(t("detail.photos_error"));
    }
    e.target.value = "";
  };

  const hasFreeText = [c.distinguishing_marks, c.clothing, c.description, c.case_notes].some((v) => v && String(v).trim());
  const tv = (field, value) => (showTrans && trans && trans[field] ? trans[field] : value);

  const fetchTranslation = async () => {
    if (showTrans) { setShowTrans(false); return; }
    if (trans) { setShowTrans(true); return; }
    setTransLoading(true);
    try {
      const { data } = await api.get(`/cases/${kindPath}/${id}/translation`, { params: { lang } });
      setTrans(data.fields || {});
      setShowTrans(true);
    } catch {
      toast.error(t("detail.translate_error"));
    } finally {
      setTransLoading(false);
    }
  };

  const exportPdf = async () => {
    try {
      const res = await api.get(`/cases/${kindPath}/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${c.reference_code || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(t("detail.pdf_error"));
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-6 py-10" data-testid="case-detail-page">
      <button
        onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/dashboard"))}
        className="no-print inline-flex items-center gap-1 font-body text-sm text-[#5C6873] hover:text-[#1A2024]"
        data-testid="detail-back"
      >
        <ArrowLeft size={14} /> {t("detail.back_list")}
      </button>

      <div className="mt-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-body text-xs uppercase tracking-[0.18em] text-[#6B7F82]">{t("detail.eyebrow")}</p>
          <h1 className="mt-1 font-heading text-3xl font-semibold text-[#1A2024]" data-testid="detail-title">{title}</h1>
          <div className="mt-1 flex items-center gap-2">
            {c.reference_code && <span className="font-mono text-xs text-[#6B7F82]" data-testid="detail-reference">{c.reference_code}</span>}
            <KindBadge kind={kind} />
            {c.imported && <Badge variant="outline" className="rounded-full border-[#E2E8F0] text-[11px]">{t("detail.imported")}</Badge>}
          </div>
        </div>
        <div className="no-print flex flex-wrap gap-2">
          <Button variant="outline" className="border-[#E2E8F0] text-[#2E3A46]" onClick={copyLink} data-testid="detail-copy-btn">
            <Link2 size={14} className="mr-1" /> {t("detail.copy_link")}
          </Button>
          <Button variant="outline" className="border-[#E2E8F0] text-[#2E3A46]" onClick={() => window.print()} data-testid="detail-print-btn">
            <Printer size={14} className="mr-1" /> {t("detail.print")}
          </Button>
          <Button variant="outline" className="border-[#E2E8F0] text-[#2E3A46]" onClick={exportPdf} data-testid="detail-export-pdf-btn">
            <Download size={14} className="mr-1" /> {t("detail.export_pdf")}
          </Button>
          {canEdit && (
            <Link to={editPath} data-testid="detail-edit-btn">
              <Button variant="outline" className="border-[#E2E8F0] text-[#2E3A46]"><Pencil size={14} className="mr-1" /> {t("common.edit")}</Button>
            </Link>
          )}
        </div>
      </div>

      <div className="mt-8 space-y-5">
        <div className="flex gap-5">
          <div className="flex-shrink-0">
            <div className="h-40 w-40 overflow-hidden rounded-lg border border-[#E2E8F0] bg-[#F8F9FA]">
              {activePhoto ? (
                <img src={fileUrl(activePhoto)} alt="" className="h-full w-full object-cover" data-testid="detail-photo" />
              ) : (
                <div className="grid h-full w-full place-items-center text-[#6B7F82]"><FileText size={28} /></div>
              )}
            </div>
            {displayPhotos.length > 1 && (
              <div className="mt-2 flex flex-wrap gap-2" data-testid="detail-photo-gallery">
                {displayPhotos.map((p, i) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setActivePhoto(p)}
                    className={`h-12 w-12 overflow-hidden rounded-md border ${activePhoto === p ? "border-[#2E3A46] ring-2 ring-[#2E3A46]/30" : "border-[#E2E8F0]"}`}
                    data-testid={`detail-photo-thumb-${i}`}
                  >
                    <img src={fileUrl(p)} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            {canManagePhotos && (
              <div className="no-print mt-3 w-40 space-y-1.5" data-testid="detail-photo-manage">
                {activePhoto && displayPhotos.length > 1 && displayPhotos[0] !== activePhoto && (
                  <button type="button" onClick={() => setAsMain(activePhoto)} className="flex w-full items-center gap-1.5 rounded-md border border-[#E2E8F0] px-2 py-1 font-body text-xs text-[#2E3A46] hover:bg-[#F8F9FA]" data-testid="detail-photo-set-main">
                    <Star size={13} /> {t("detail.photo_set_main")}
                  </button>
                )}
                {activePhoto && (
                  <button type="button" onClick={() => removePhoto(activePhoto)} className="flex w-full items-center gap-1.5 rounded-md border border-[#E2E8F0] px-2 py-1 font-body text-xs text-[#9E4747] hover:bg-[#9E4747]/[0.05]" data-testid="detail-photo-remove">
                    <Trash2 size={13} /> {t("detail.photo_remove")}
                  </button>
                )}
                <label className="flex w-full cursor-pointer items-center gap-1.5 rounded-md border border-dashed border-[#E2E8F0] px-2 py-1 font-body text-xs text-[#2E3A46] hover:bg-[#F8F9FA]" data-testid="detail-photo-add-label">
                  <Upload size={13} /> {t("detail.photo_add")}
                  <input type="file" accept="image/*" className="hidden" onChange={addPhoto} data-testid="detail-photo-add-input" />
                </label>
                {pendingPhotos !== null && (
                  <div className="mt-2 space-y-1.5 rounded-md border border-[#C08A2D]/50 bg-[#C08A2D]/[0.08] p-2" data-testid="detail-photos-unsaved">
                    <p className="font-body text-[11px] font-medium text-[#8A5200]">{t("detail.photos_unsaved")}</p>
                    <Button size="sm" className="w-full bg-[#2E3A46] text-white hover:bg-[#1A2024]" onClick={savePhotos} data-testid="detail-photos-save-btn">
                      {t("detail.photos_save")}
                    </Button>
                    <Button size="sm" variant="outline" className="w-full border-[#E2E8F0] text-[#5C6873]" onClick={cancelPhotos} data-testid="detail-photos-cancel-btn">
                      {t("detail.photos_cancel")}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="grid flex-1 grid-cols-2 gap-4 self-start">
            <Detail label={t("form.gender")} value={c.gender === "male" ? t("form.gender.male") : c.gender === "female" ? t("form.gender.female") : null} />
            <Detail label={isAnte && c.date_of_birth ? t("form.age_at_disappearance") : (c.age_min === c.age_max ? t("form.age") : t("form.age_min") + " / " + t("form.age_max"))} value={(c.age_min != null || c.age_max != null) ? (c.age_min === c.age_max ? `${c.age_min}` : `${c.age_min ?? "?"} – ${c.age_max ?? "?"}`) : null} />
            {isAnte && c.date_of_birth && <Detail label={t("form.dob")} value={fmtDob(c.date_of_birth)} />}
            <Detail label={t("form.country")} value={c.country} />
            {c.source_reference && <Detail label={t("form.source_ref")} value={c.source_reference} />}
            <Detail label={t("form.location")} value={c.location} />
            <Detail label={isAnte ? t("form.date_seen") : t("form.date_found")} value={c.event_date} />
            {isAnte && <Detail label={t("detail.reported_by")} value={c.contact_relation} />}
            {isAnte && c.contact_name && <Detail label={t("form.contact_name")} value={c.contact_name} />}
            {isAnte && c.contact_phone && <Detail label={t("form.contact_phone")} value={c.contact_phone} />}
            {isAnte && c.contact_email && <Detail label={t("form.contact_email")} value={c.contact_email} />}
            {(c.merge_history || []).map((m, i) => (
              <Detail key={`mh-${i}`} label={t("detail.merged_from")} value={`${m.source_file} (${(m.merged_at || "").slice(0, 10)})`} />
            ))}
            {!isAnte && c.contributor_org && <Detail label={t("case.submitted_by")} value={c.contributor_org} />}
          </div>
        </div>

        <Block title={t("form.section.physical")}>
          <Detail label={t("form.height")} value={(c.height_min != null || c.height_max != null) ? `${c.height_min ?? "?"} – ${c.height_max ?? "?"} cm` : (c.height_cm ? `${c.height_cm} cm` : (c.height_bucket || null))} />
          <Detail label={t("form.weight")} value={c.weight_kg ? `${c.weight_kg} kg` : null} />
          <Detail label={t("form.hair")} value={attrLabel(t, c.hair_color)} />
          <Detail label={t("form.eye")} value={attrLabel(t, c.eye_color)} />
          <Detail label={t("form.ethnicity")} value={c.ethnicity} />
          {!isAnte && <Detail label={t("form.pmi")} value={c.estimated_pmi_days} />}
        </Block>

        {hasFreeText && (
          <div className="no-print flex flex-wrap items-center gap-2" data-testid="detail-translate-row">
            <Button
              variant="outline"
              size="sm"
              className="border-[#E2E8F0] text-[#2E3A46]"
              onClick={fetchTranslation}
              disabled={transLoading}
              data-testid="detail-translate-btn"
            >
              {transLoading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Languages size={14} className="mr-1" />}
              {showTrans ? t("detail.show_original") : t("detail.translate")}
            </Button>
            {showTrans && (
              <Badge variant="outline" className="rounded-full border-[#4A7C59]/40 text-[11px] text-[#4A7C59]" data-testid="detail-translated-badge">
                {t("detail.translated_badge")}
              </Badge>
            )}
          </div>
        )}

        <Block title={t("form.marks")}>
          <div className="sm:col-span-2"><Detail label={t("form.marks")} value={tv("distinguishing_marks", c.distinguishing_marks)} /></div>
          <div className="sm:col-span-2"><Detail label={t("form.clothing")} value={tv("clothing", c.clothing)} /></div>
        </Block>

        <Block title={isAnte ? t("form.description.ante") : t("form.description.post")}>
          <div className="sm:col-span-2"><Detail label={isAnte ? t("form.description.ante") : t("form.description.post")} value={tv("description", c.description)} /></div>
          {!isAnte && <div className="sm:col-span-2"><Detail label={t("form.case_notes")} value={tv("case_notes", c.case_notes)} /></div>}
          {c.source_url && (
            <div className="sm:col-span-2">
              <div className="font-body text-xs uppercase tracking-[0.12em] text-[#6B7F82]">{t("detail.source")}</div>
              <a href={c.source_url} target="_blank" rel="noreferrer" className="mt-0.5 inline-flex items-center gap-1 font-body text-sm text-[#2E3A46] hover:underline" data-testid="detail-source-link">
                <ExternalLink size={13} /> {c.source_url}
              </a>
            </div>
          )}
        </Block>
      </div>
    </div>
  );
}
